import API_CONFIG from "../../config/api";

const base = API_CONFIG.BASE_URL;
const token = () => localStorage.getItem("authToken");
const authHeaders = (json = false) => ({
  Authorization: `Bearer ${token()}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

const parse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || `Erreur ${response.status}`);
  return data;
};

export async function listEcommerceOrders(params) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== "" && value != null));
  return parse(await fetch(`${base}/api/ecommerce/orders?${query}`, { headers: authHeaders() }));
}
export async function getEcommerceOrder(id) {
  const data = await parse(await fetch(`${base}/api/ecommerce/orders/${id}`, { headers: authHeaders() }));
  const order = data.order || {};
  const parseAddress = (value) => {
    if (!value || typeof value !== "string") return value || null;
    try { return JSON.parse(value); } catch { return null; }
  };
  return {
    ...order,
    items: data.items || [],
    fulfillments: data.fulfillments || [],
    payment_summary: data.payment_summary || null,
    payments: data.payments || [],
    shipping_address: parseAddress(order.shipping_address),
    billing_address: parseAddress(order.billing_address),
  };
}
export async function getEcommerceOrderStats(warehouseId) {
  return parse(await fetch(`${base}/api/ecommerce/orders/stats?warehouse_id=${warehouseId}`, { headers: authHeaders() }));
}
export async function updateEcommerceOrderStatus(id, status) {
  return parse(await fetch(`${base}/api/ecommerce/orders/${id}/status`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify({ status }) }));
}
export async function updateEcommerceOrder(id, payload) {
  return parse(await fetch(`${base}/api/ecommerce/orders/${id}`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify(payload) }));
}
export async function deleteEcommerceOrder(id, reason, confirm) {
  return parse(await fetch(`${base}/api/ecommerce/orders/${id}`, { method: "DELETE", headers: authHeaders(true), body: JSON.stringify({ reason, confirm }) }));
}
export async function getEcommerceOrderPaymentOptions(id) {
  return parse(await fetch(`${base}/api/ecommerce/orders/${id}/payment-options`, { headers: authHeaders() }));
}
export async function collectEcommerceOrderPayment(id, payload) {
  return parse(await fetch(`${base}/api/ecommerce/orders/${id}/collect-payment`, {
    method: "POST",
    headers: {
      ...authHeaders(true),
      "X-Idempotency-Key": `ecommerce-mobile-${id}-${payload?.payment_status || "capture"}`,
    },
    body: JSON.stringify(payload),
  }));
}
export async function refundEcommerceOrderPayment(id, payload) {
  return parse(await fetch(`${base}/api/ecommerce/orders/${id}/refund`, {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  }));
}

export async function listEcommerceProducts(params) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== "" && value != null && value !== false));
  return parse(await fetch(`${base}/api/produits/ecommerce?${query}`, { headers: authHeaders(true) }));
}
export async function listEcommerceCategories(companyId) {
  const data = await parse(await fetch(`${base}/api/categories?company_id=${companyId}`, { headers: authHeaders() }));
  return Array.isArray(data) ? data : data.categories || [];
}
export async function updateEcommerceProduct(id, warehouseId, payload, storeId = null) {
  return parse(await fetch(`${base}/api/produits/${id}/online`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify({ ...payload, warehouse_id: warehouseId, ...(storeId ? { store_id: storeId } : {}) }) }));
}
export async function bulkEcommerceProducts(action, warehouseId, value, storeId = null) {
  const path = action === "visibility" ? "online-visibility" : "online-featured";
  const key = action === "visibility" ? "online_visibility" : "online_featured";
  return parse(await fetch(`${base}/api/produits/bulk/${path}`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify({ warehouse_id: warehouseId, ...(action === "featured" && storeId ? { store_id: storeId } : {}), [key]: value, action_type: "toggle_only" }) }));
}
export async function listEcommerceFeaturedStores(warehouseId) {
  const data = await parse(await fetch(`${base}/api/ecommerce/stores/catalog-display-order?warehouse_id=${warehouseId}`, { headers: authHeaders() }));
  return data.stores || [];
}

export async function listEcommerceStores(warehouseId) {
  const data = await parse(await fetch(`${base}/api/ecommerce/stores?warehouse_id=${warehouseId}`, { headers: authHeaders(true) }));
  return data.stores || [];
}
export async function listAvailableEcommerceWarehouses() {
  const data = await parse(await fetch(`${base}/api/ecommerce/stores/warehouses/available`, { headers: authHeaders(true) }));
  return data.warehouses || [];
}
export async function getEcommerceStore(id) {
  const data = await parse(await fetch(`${base}/api/ecommerce/stores/${id}`, { headers: authHeaders() }));
  return data.store || null;
}
export async function saveEcommerceStore(id, formData) {
  return parse(await fetch(`${base}/api/ecommerce/stores${id ? `/${id}` : ""}`, { method: id ? "PUT" : "POST", headers: authHeaders(), body: formData }));
}
export async function toggleEcommerceStore(id, isActive) {
  return parse(await fetch(`${base}/api/ecommerce/stores/${id}/status`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify({ is_active: isActive ? 1 : 0 }) }));
}
export const publicStoreUrl = (store) =>
  API_CONFIG.getStoreMetaUrl(
    store?.store_slug || "",
    store?.logo_url || "",
    store?.domain || "",
    `${store?.store_name || ""}:${store?.store_description || ""}`
  );

export async function saveEcommerceTheme(id, themeConfig, delivery, bannerFile) {
  const formData = new FormData();
  formData.append("theme_config", JSON.stringify(themeConfig));
  if (bannerFile instanceof File) formData.append("banners", bannerFile);
  await parse(await fetch(`${base}/api/ecommerce/stores/${id}/theme`, { method: "PUT", headers: authHeaders(), body: formData }));
  return parse(await fetch(`${base}/api/ecommerce/stores/${id}`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify(delivery) }));
}
export async function getEcommercePaymentGateway(storeId) {
  return parse(await fetch(`${base}/api/ecommerce/payment-gateways/${storeId}`, { headers: authHeaders() }));
}
export async function saveEcommercePaymentGateway(storeId, payload) {
  return parse(await fetch(`${base}/api/ecommerce/payment-gateways/${storeId}`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify(payload) }));
}

export async function listEcommerceReviews(params) {
  const apiParams = {
    page: params.page,
    limit: params.limit,
    warehouse_id: params.warehouse_id,
    search: params.search,
    status: params.status,
    rating: params.rating,
    storeId: params.store_id,
    dateFrom: params.start_date,
    dateTo: params.end_date,
  };
  const query = new URLSearchParams(Object.entries(apiParams).filter(([, value]) => value !== "" && value != null));
  const response = await parse(await fetch(`${base}/api/ecommerce/reviews?${query}`, { headers: authHeaders() }));
  if (Array.isArray(response.reviews)) return { ...response, reviews: response.reviews.map((review) => ({ ...review, customer_name: review.customer_full_name || review.customer_name })) };
  const data = response.data || {};
  return {
    reviews: (data.reviews || []).map((review) => ({ ...review, customer_name: review.customer_full_name || review.customer_name })),
    total: Number(data.pagination?.total || 0),
  };
}
export async function getEcommerceReview(id) {
  const response = await parse(await fetch(`${base}/api/ecommerce/reviews/${id}`, { headers: authHeaders() }));
  const review = response.data?.review || response.review || response;
  return { ...review, customer_name: review.customer_full_name || review.customer_name };
}
export async function getEcommerceReviewStats(warehouseId) {
  const response = await parse(await fetch(`${base}/api/ecommerce/reviews/statistics/global?warehouse_id=${warehouseId}`, { headers: authHeaders() }));
  if (!response.data?.summary) return response;
  const summary = response.data.summary;
  return { total: summary.total, pending: summary.pending, approved: summary.approved, rejected: summary.rejected, spam: summary.spam, average_rating: summary.averageRating };
}
export async function moderateEcommerceReview(id, action, moderationReason) {
  return parse(await fetch(`${base}/api/ecommerce/reviews/${id}/moderate`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify({ action, moderationReason, moderation_reason: moderationReason }) }));
}
export async function respondEcommerceReview(id, merchantResponse) {
  return parse(await fetch(`${base}/api/ecommerce/reviews/${id}/respond`, { method: "POST", headers: authHeaders(true), body: JSON.stringify({ response: merchantResponse, merchant_response: merchantResponse }) }));
}
export async function deleteEcommerceReview(id) {
  return parse(await fetch(`${base}/api/ecommerce/reviews/${id}`, { method: "DELETE", headers: authHeaders() }));
}
